// src/components/lesson/QuizView.jsx
import { useState, useEffect, useRef } from 'react';
import { lmsAPI } from '../../services/api';

export default function QuizView({ lesson, enrollmentId, done, onComplete }) {
  const [attempts, setAttempts]   = useState([]);
  const [attempt,  setAttempt]    = useState(null); // active attempt
  const [answers,  setAnswers]    = useState({});   // { questionId: [optionId] }
  const [result,   setResult]     = useState(null);
  const [loading,  setLoading]    = useState(false);
  const [timeLeft, setTimeLeft]   = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    lmsAPI.myAttempts(lesson.id, enrollmentId)
      .then(r => setAttempts(r.data.data || []))
      .catch(() => {});
  }, [lesson.id, enrollmentId]);

  // Timer
  useEffect(() => {
    if (!attempt || !timeLeft) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [attempt]);

  const startQuiz = async () => {
    setLoading(true);
    try {
      const r = await lmsAPI.startQuiz(lesson.id, { enrollment_id: enrollmentId });
      setAttempt(r.data.data);
      setAnswers({});
      setResult(null);
      if (r.data.data.time_limit) setTimeLeft(r.data.data.time_limit);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to start quiz');
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    clearInterval(timerRef.current);
    setLoading(true);
    try {
      const answersArr = Object.entries(answers).map(([qid, ans]) => ({
        question_id: qid, answer: Array.isArray(ans) ? ans : [ans],
      }));
      const r = await lmsAPI.submitQuiz(attempt.attempt_id, {
        answers: answersArr,
        time_taken: (attempt.time_limit || 0) - (timeLeft || 0),
      });
      setResult(r.data.data);
      setAttempt(null);
      const newAttempts = await lmsAPI.myAttempts(lesson.id, enrollmentId);
      setAttempts(newAttempts.data.data || []);
      if (r.data.data.passed) onComplete();
    } catch (e) { alert(e.response?.data?.error || 'Submit failed'); } finally { setLoading(false); }
  };

  const toggleAnswer = (qid, optId) => {
    setAnswers(prev => {
      const curr = prev[qid] || [];
      return { ...prev, [qid]: curr.includes(optId) ? curr.filter(x=>x!==optId) : [...curr, optId] };
    });
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  // ── Previous attempts summary ────────────────────────
  if (result || (attempts.length > 0 && !attempt)) {
    const latest = result || attempts[0];
    const passed = latest.passed;
    const canRetry = attempts.length < (lesson.max_attempts || 3) && !passed;

    return (
      <div style={{ maxWidth:640 }}>
        {/* Result card */}
        <div style={{ background: passed ? 'rgba(62,207,142,0.08)' : 'rgba(240,96,96,0.08)', border:`1px solid ${passed?'rgba(62,207,142,0.25)':'rgba(240,96,96,0.25)'}`, borderRadius:'var(--r-xl)', padding:28, marginBottom:24, textAlign:'center' }}>
          <div style={{ fontSize:'3rem', marginBottom:12 }}>{passed ? '🎉' : '📚'}</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:600, marginBottom:6 }}>
            {passed ? 'Quiz Passed!' : 'Not quite there yet'}
          </div>
          <div style={{ fontSize:'2.5rem', fontWeight:700, color: passed ? 'var(--teal)' : 'var(--red)', marginBottom:4 }}>
            {latest.score}%
          </div>
          <div style={{ fontSize:'0.85rem', color:'var(--t-muted)' }}>
            Pass score: {lesson.pass_score || 70}% · Attempt {latest.attempt_no || attempts.length} of {lesson.max_attempts || 3}
          </div>
        </div>

        {/* Answer review */}
        {result?.answers && (
          <div style={{ marginBottom:24 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:600, marginBottom:14 }}>Review</div>
            {result.answers.filter(a => a.question_id).map((a, i) => (
              <div key={i} style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'14px 16px', marginBottom:8 }}>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:20, background: a.correct?'rgba(62,207,142,0.12)':'rgba(240,96,96,0.12)', color: a.correct?'var(--teal)':'var(--red)', border:`1px solid ${a.correct?'rgba(62,207,142,0.3)':'rgba(240,96,96,0.3)'}` }}>
                    {a.correct ? '✓ Correct' : '✗ Wrong'}
                  </span>
                </div>
                {a.correct_answers && (
                  <div style={{ fontSize:'0.8rem', color:'var(--t-muted)' }}>
                    Correct: {a.correct_answers.map(x=>x.toUpperCase()).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {canRetry ? (
          <button className="btn btn-primary" onClick={startQuiz} disabled={loading} style={{ padding:'12px 28px' }}>
            {loading ? 'Starting…' : 'Try Again →'}
          </button>
        ) : passed ? (
          <div className="badge badge-green" style={{ padding:'8px 16px', fontSize:'0.82rem' }}>✓ Completed</div>
        ) : (
          <div style={{ fontSize:'0.85rem', color:'var(--t-muted)' }}>Maximum attempts reached.</div>
        )}
      </div>
    );
  }

  // ── Start screen ─────────────────────────────────────
  if (!attempt) return (
    <div style={{ maxWidth:560 }}>
      <div className="card" style={{ padding:28, marginBottom:20 }}>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {[
            ['Pass score',    `${lesson.pass_score || 70}%`],
            ['Max attempts',  `${lesson.max_attempts || 3}`],
            ['Attempts used', `${attempts.length}`],
            ...(lesson.estimated_minutes ? [['Time limit', `${lesson.estimated_minutes} min`]] : []),
          ].map(([k,v]) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.88rem' }}>
              <span style={{ color:'var(--t-muted)' }}>{k}</span>
              <span style={{ fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
      {lesson.instructions && (
        <div style={{ marginBottom:20, padding:'14px 18px', background:'var(--gold-dim)', borderRadius:'var(--r-md)', border:'1px solid var(--gold-border)', fontSize:'0.88rem', color:'var(--t-secondary)', lineHeight:1.7 }} dangerouslySetInnerHTML={{ __html: lesson.instructions }}/>
      )}
      <button className="btn btn-primary" onClick={startQuiz} disabled={loading || attempts.length >= (lesson.max_attempts||3)}
        style={{ padding:'13px 32px', fontSize:'0.95rem', fontWeight:600 }}>
        {loading ? 'Starting…' : 'Start Quiz →'}
      </button>
    </div>
  );

  // ── Active quiz ───────────────────────────────────────
  const questions = attempt.questions || [];
  const answered  = Object.keys(answers).length;

  return (
    <div style={{ maxWidth:680 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, padding:'12px 16px', background:'var(--bg-2)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
        <div style={{ fontSize:'0.85rem', color:'var(--t-muted)' }}>{answered} / {questions.length} answered</div>
        {timeLeft !== null && (
          <div style={{ fontFamily:'var(--font-mono)', fontSize:'1rem', fontWeight:600, color: timeLeft < 60 ? 'var(--red)' : 'var(--t-primary)' }}>
            ⏱ {fmt(timeLeft)}
          </div>
        )}
      </div>

      {/* Questions */}
      {questions.map((q, qi) => (
        <div key={q.id} style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'20px 22px', marginBottom:16 }}>
          <div style={{ fontSize:'0.75rem', color:'var(--t-muted)', marginBottom:8 }}>Q{qi+1} · {q.points} pt{q.points>1?'s':''}</div>
          <div style={{ fontWeight:500, fontSize:'1rem', lineHeight:1.6, marginBottom:16 }}>{q.question_text}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {(q.options||[]).map(opt => {
              const selected = (answers[q.id]||[]).includes(opt.id);
              return (
                <button key={opt.id} onClick={() => toggleAnswer(q.id, opt.id)}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', borderRadius:'var(--r-md)', border:`1px solid ${selected?'var(--gold)':'var(--border)'}`, background: selected?'var(--gold-dim)':'var(--bg-3)', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>
                  <div style={{ width:22, height:22, borderRadius:'50%', border:`2px solid ${selected?'var(--gold)':'var(--border-2)'}`, background: selected?'var(--gold)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:11, color:'#0c0d0f', fontWeight:700 }}>
                    {selected ? '✓' : opt.id.toUpperCase()}
                  </div>
                  <span style={{ fontSize:'0.9rem', color: selected?'var(--t-primary)':'var(--t-secondary)' }}>{opt.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Submit */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:8 }}>
        <span style={{ fontSize:'0.82rem', color:'var(--t-muted)' }}>{answered}/{questions.length} questions answered</span>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || answered === 0}
          style={{ padding:'12px 28px', fontSize:'0.95rem', fontWeight:600 }}>
          {loading ? 'Submitting…' : 'Submit Quiz →'}
        </button>
      </div>
    </div>
  );
}
